import { ArrowUp, ChevronRight, Code2, Workflow, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import adkPreviewImg from '/bot-type-adk-preview.png'
import studioPreviewImg from '/bot-type-studio-preview.png'
import { trackEvent } from '~/providers'

export type BotType = 'studio' | 'adk' | 'vibe'

export const DEFAULT_BOT_TYPES = ['studio', 'adk'] as const satisfies BotType[]

// Example prompts cycled in the "Build from a prompt" preview, mirroring the standalone Viber entry screen.
const VIBE_PROMPT_EXAMPLES = [
  'A refund agent for an e-commerce store',
  'A scheduling assistant for a dentist office',
  'A lead qualifier for a SaaS landing page',
  'A FAQ bot for our developer docs',
]

type BotTypeSelectionOverlayProps = {
  onSelect: (type: BotType) => void
  onClose: () => void
  dismissable?: boolean
  botTypes?: readonly BotType[]
}

type BotTypeSelectionScreenProps = {
  onSelect: (type: BotType) => void
  onClose?: () => void
  dismissable?: boolean
  botTypes?: readonly BotType[]
}

const trackBotTypeDialogPresented = () => trackEvent({ type: 'bot_type_dialog_presented' })
// The experiment is keyed on "viber" in analytics even though the UI never says it (see PD-427).
const trackBotTypeSelected = (botType: BotType) =>
  trackEvent({ type: 'bot_type_selected', botType: botType === 'vibe' ? 'viber' : botType })
const trackBotTypeDialogDismissed = () => trackEvent({ type: 'bot_type_dialog_dismissed' })

const useBotTypeSelectionEffects = ({
  dismissable,
  onClose,
}: Pick<BotTypeSelectionScreenProps, 'dismissable' | 'onClose'>) => {
  useEffect(() => {
    trackBotTypeDialogPresented()
  }, [])

  useEffect(() => {
    if (!dismissable || !onClose) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [dismissable, onClose])
}

// Decorative type/delete animation over the example prompts. Purely visual — the card click
// still routes into the Viber builder; nothing is seeded from the animated text.
const useTypingPlaceholder = (examples: string[]) => {
  const [text, setText] = useState('')
  const state = useRef({ idx: 0, charIdx: 0, deleting: false })

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>

    const tick = () => {
      const s = state.current
      const current = examples[s.idx] ?? ''

      if (!s.deleting) {
        if (s.charIdx < current.length) {
          s.charIdx += 1
          setText(current.slice(0, s.charIdx))
          timeout = setTimeout(tick, 38)
        } else {
          s.deleting = true
          timeout = setTimeout(tick, 2200)
        }
      } else if (s.charIdx > 0) {
        s.charIdx -= 1
        setText(current.slice(0, s.charIdx))
        timeout = setTimeout(tick, 18)
      } else {
        s.deleting = false
        s.idx = (s.idx + 1) % examples.length
        timeout = setTimeout(tick, 500)
      }
    }

    timeout = setTimeout(tick, 600)
    return () => clearTimeout(timeout)
  }, [examples])

  return text
}

const VibePromptPreview = () => {
  const typed = useTypingPlaceholder(VIBE_PROMPT_EXAMPLES)

  return (
    <div className="relative flex min-h-56 flex-col items-center justify-center gap-5 overflow-hidden rounded-[1.5rem] bg-gradient-to-br from-violet-300 via-fuchsia-300 to-pink-300 p-6 sm:min-h-72 sm:p-8">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_18%,rgba(196,181,253,0.65),transparent_55%),radial-gradient(circle_at_85%_90%,rgba(249,168,212,0.6),transparent_55%)]" />
      <p className="relative text-center text-xl font-semibold text-white drop-shadow-sm sm:text-2xl">
        What agent should we build?
      </p>
      <div className="relative flex w-full max-w-md items-center gap-2 rounded-2xl border border-white/20 bg-slate-900/45 px-4 py-3 shadow-lg backdrop-blur-sm">
        <span className="min-h-5 flex-1 text-left text-sm text-white/95">
          {typed}
          <span className="ml-px inline-block h-4 w-px translate-y-0.5 animate-pulse bg-white/90 align-middle" />
        </span>
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-slate-900">
          <ArrowUp className="h-4 w-4" />
        </span>
      </div>
    </div>
  )
}

const VibeHeroCard = ({ onSelect }: { onSelect: (type: BotType) => void }) => (
  <div className="group relative">
    <div className="pointer-events-none absolute -inset-2 rounded-[2rem] bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-20" />
    <button
      type="button"
      onClick={() => onSelect('vibe')}
      className="relative grid w-full gap-6 overflow-hidden rounded-[1.75rem] border border-violet-200/70 bg-gradient-to-br from-violet-50 via-background to-sky-50 p-5 text-left transition-all duration-200 hover:-translate-y-px hover:border-violet-300 hover:shadow-[0_28px_70px_-50px_rgba(139,92,246,0.8)] sm:p-6 md:grid-cols-2 md:items-stretch dark:border-violet-500/20 dark:from-violet-950/40 dark:via-background dark:to-sky-950/30"
    >
      <div className="flex flex-col justify-between gap-6">
        <div className="space-y-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-violet-600 to-pink-500 px-3 py-1 text-xs font-semibold text-white">
            <span className="h-1.5 w-1.5 rounded-full bg-white" />
            Research Preview
          </span>
          <h2 className="text-3xl font-semibold tracking-tight text-foreground sm:text-[2.125rem]">
            Build from a prompt
          </h2>
          <p className="max-w-md text-sm leading-6 text-muted-foreground">
            We&apos;ll turn your prompt into a working AI agent you can review and refine.
          </p>
        </div>

        <div className="flex flex-col text-xs">
          <span className="font-medium tracking-[0.18em] text-muted-foreground uppercase">Best for</span>
          <span className="mt-1 text-sm text-foreground/80">
            Step-by-step guidance without a steep learning curve or code.
          </span>
        </div>
      </div>

      <VibePromptPreview />
    </button>
  </div>
)

const SecondaryOptionCard = ({
  onClick,
  icon,
  title,
  badges,
  description,
  previewSrc,
  previewAlt,
}: {
  onClick: () => void
  icon: React.ReactNode
  title: string
  badges?: React.ReactNode
  description: string
  previewSrc: string
  previewAlt: string
}) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex w-full items-center gap-4 rounded-2xl border border-border bg-card p-4 text-left transition-colors hover:border-border/80 hover:bg-accent/40"
  >
    <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-border bg-background text-muted-foreground transition-colors group-hover:text-foreground">
      {icon}
    </span>

    <div className="min-w-0 flex-1 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-base font-semibold text-foreground">{title}</span>
        {badges}
      </div>
      <p className="text-sm leading-5 text-muted-foreground">{description}</p>
    </div>

    <div className="hidden w-28 shrink-0 overflow-hidden rounded-lg border border-border/60 sm:block">
      <img src={previewSrc} alt={previewAlt} className="h-16 w-full object-cover" />
    </div>

    <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
  </button>
)

export const BotTypeSelectionScreen = ({
  onSelect,
  onClose,
  dismissable = true,
  botTypes = DEFAULT_BOT_TYPES,
}: BotTypeSelectionScreenProps) => {
  useBotTypeSelectionEffects({ dismissable, onClose })
  const showVibe = botTypes.includes('vibe')
  const showStudio = botTypes.includes('studio')
  const showAdk = botTypes.includes('adk')

  const adkBadges = (
    <>
      <span className="rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
        Advanced
      </span>
      <span className="rounded border border-blue-500 bg-blue-950 px-2 py-0.5 text-xs font-semibold text-blue-400">
        Beta
      </span>
    </>
  )

  // Viber experiment (PD-427): a prompt-first hero with Studio / ADK demoted to secondary options.
  if (showVibe) {
    return (
      <div className="flex w-full flex-col gap-8">
        <div className="flex flex-col items-center gap-2 text-center">
          <h1
            id="bot-type-dialog-title"
            className="font-[Aspekta] text-4xl font-semibold tracking-tight text-balance text-foreground sm:text-5xl"
          >
            How do you want to build?
          </h1>
          <p className="mx-auto max-w-2xl text-sm leading-6 text-muted-foreground">
            Choose what&apos;s best for you. This can&apos;t be changed later.
          </p>
        </div>

        <VibeHeroCard onSelect={onSelect} />

        {showStudio || showAdk ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-medium tracking-[0.18em] text-muted-foreground uppercase">Or</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {showStudio ? (
              <SecondaryOptionCard
                onClick={() => onSelect('studio')}
                icon={<Workflow className="h-[18px] w-[18px]" />}
                title="Botpress Studio"
                description="Build bots visually with a drag and drop canvas."
                previewSrc={studioPreviewImg}
                previewAlt="Botpress Studio preview"
              />
            ) : null}

            {showAdk ? (
              <SecondaryOptionCard
                onClick={() => onSelect('adk')}
                icon={<Code2 className="h-[18px] w-[18px]" />}
                title="Agent Development Kit"
                badges={adkBadges}
                description="Build agents in TypeScript with full control."
                previewSrc={adkPreviewImg}
                previewAlt="Agent Development Kit preview"
              />
            ) : null}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="relative w-full overflow-hidden rounded-[1.75rem] border border-border/70 bg-card/95 shadow-[0_32px_100px_-48px_rgba(15,23,42,0.7)] backdrop-blur">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,#dbeafe_0%,transparent_45%),radial-gradient(circle_at_bottom,#eff6ff_0%,transparent_38%)] opacity-90 dark:bg-[radial-gradient(circle_at_top,#172554_0%,transparent_50%),radial-gradient(circle_at_bottom,#0f172a_0%,transparent_40%)]" />
      <div className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

      <div className="relative flex flex-col gap-4 p-4 sm:gap-5 sm:p-5 md:p-6">
        {dismissable && onClose ? (
          <button
            type="button"
            aria-label="Close bot type selection"
            onClick={onClose}
            className="absolute top-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-background/80 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}

        <div className="flex flex-col items-center gap-2 text-center">
          <div className="space-y-2">
            <h1
              id="bot-type-dialog-title"
              className="font-[Aspekta] text-3xl font-semibold tracking-tight text-balance text-foreground sm:text-4xl lg:text-[3.5rem]"
            >
              How do you want to build?
            </h1>
            <p className="mx-auto max-w-2xl text-sm leading-5 text-muted-foreground sm:leading-6">
              Choose what&apos;s best for you. This can&apos;t be changed later.
            </p>
          </div>
        </div>

        {showStudio ? (
          <div className="group relative">
            <div className="pointer-events-none absolute -inset-2 rounded-[1.75rem] bg-gradient-to-r from-cyan-400 via-blue-500 to-blue-600 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-22" />
            <button
              type="button"
              onClick={() => onSelect('studio')}
              className="relative flex w-full flex-col gap-3 overflow-hidden rounded-[1.5rem] border border-primary/20 bg-background/85 p-4 text-left transition-all duration-200 hover:-translate-y-px hover:border-primary/30 hover:shadow-[0_22px_48px_-52px_rgba(59,130,246,0.72)] sm:p-5 md:gap-4 md:p-5"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-transparent to-primary/12 opacity-80" />
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-xl font-semibold text-foreground sm:text-2xl">Botpress Studio</h2>
                  <span className="rounded-md bg-emerald-500/12 px-2.5 py-1 text-sm font-semibold text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-300">
                    Recommended
                  </span>
                </div>
                <p className="max-w-2xl text-sm leading-5 text-muted-foreground sm:leading-6">
                  Build bots visually with a drag and drop canvas.
                </p>
              </div>

              <div className="relative overflow-hidden rounded-[1rem]">
                <img
                  src={studioPreviewImg}
                  alt="Botpress Studio preview"
                  className="h-[24vh] max-h-64 min-h-40 w-full rounded-[1rem] object-cover sm:h-[26vh] sm:max-h-72"
                />
              </div>

              <div className="flex flex-col text-xs">
                <span className="font-medium tracking-[0.18em] text-muted-foreground uppercase">Best for</span>
                <span className="mt-1 text-sm text-muted-foreground">Most use cases.</span>
              </div>
            </button>
          </div>
        ) : null}

        {showAdk ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="rounded-full border border-border bg-background px-3 py-1 text-xs font-medium tracking-[0.18em] text-muted-foreground uppercase">
                Or
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="group relative">
              <div className="pointer-events-none absolute -inset-2 rounded-[1.5rem] bg-gradient-to-r from-purple-500 via-violet-500 to-fuchsia-500 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-18" />
              <button
                type="button"
                onClick={() => onSelect('adk')}
                className="relative flex w-full flex-col gap-3 rounded-[1.25rem] border border-transparent bg-transparent p-1 text-left transition-colors hover:border-border/65 hover:bg-background/40 sm:p-2 md:flex-row md:items-center md:justify-between"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-background text-muted-foreground transition-colors group-hover:text-foreground">
                    <Code2 className="h-[18px] w-[18px]" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground sm:text-base">
                        I&apos;m a developer
                      </span>
                      <span className="text-sm font-semibold text-foreground underline decoration-border underline-offset-4 sm:text-base">
                        Use the Agent Development Kit
                      </span>
                      <span className="rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                        Advanced
                      </span>
                      <span className="rounded border border-blue-500 bg-blue-950 px-2 py-0.5 text-xs font-semibold text-blue-400">
                        Beta
                      </span>
                    </div>
                    <p className="max-w-2xl text-sm leading-5 text-muted-foreground">
                      Build agents in TypeScript with full control.
                    </p>
                  </div>
                </div>

                <div className="hidden overflow-hidden rounded-xl md:block md:w-40">
                  <img
                    src={adkPreviewImg}
                    alt="Agent Development Kit preview"
                    className="w-full rounded-xl object-cover"
                  />
                </div>
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

const BotTypeSelectionOverlay = ({ onSelect, onClose, dismissable = true, botTypes }: BotTypeSelectionOverlayProps) => {
  const fullscreen = (botTypes ?? DEFAULT_BOT_TYPES).includes('vibe')

  if (fullscreen) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bot-type-dialog-title"
        className="fixed inset-0 z-50 overflow-y-auto bg-background"
      >
        {dismissable && onClose ? (
          <button
            type="button"
            aria-label="Close bot type selection"
            onClick={onClose}
            className="absolute top-5 right-5 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        ) : null}

        <div className="mx-auto flex min-h-dvh w-full max-w-5xl flex-col justify-center px-6 py-16 sm:px-10">
          <BotTypeSelectionScreen onSelect={onSelect} onClose={onClose} dismissable={dismissable} botTypes={botTypes} />
        </div>
      </div>
    )
  }

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (dismissable && event.target === event.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bot-type-dialog-title"
      className="fixed inset-0 z-50 overflow-y-auto bg-background/80 backdrop-blur-sm"
    >
      <div
        className="flex min-h-dvh items-center justify-center px-3 py-3 sm:px-4 sm:py-4"
        onClick={handleBackdropClick}
      >
        <div className="w-full max-w-6xl">
          <BotTypeSelectionScreen onSelect={onSelect} onClose={onClose} dismissable={dismissable} botTypes={botTypes} />
        </div>
      </div>
    </div>
  )
}

type ShowBotTypeSelectionDialogOptions = {
  dismissable?: boolean
  botTypes?: readonly BotType[]
}

export const showBotTypeSelectionDialog = (
  options: ShowBotTypeSelectionDialogOptions = {}
): Promise<BotType | undefined> => {
  const { dismissable = true, botTypes } = options

  return new Promise((resolve) => {
    const container = document.createElement('div')
    document.body.appendChild(container)

    let reactRoot: { unmount: () => void; render: (children: React.ReactNode) => void } | null = null

    const cleanup = () => {
      reactRoot?.unmount()
      container.remove()
    }

    const handleSelect = (type: BotType) => {
      trackBotTypeSelected(type)
      cleanup()
      resolve(type)
    }

    const handleClose = () => {
      trackBotTypeDialogDismissed()
      cleanup()
      resolve(undefined)
    }

    import('react-dom/client').then(({ createRoot }) => {
      reactRoot = createRoot(container)
      reactRoot.render(
        <BotTypeSelectionOverlay
          onSelect={handleSelect}
          onClose={handleClose}
          dismissable={dismissable}
          botTypes={botTypes}
        />
      )
    })
  })
}
